"use client";

import React, { useState, useCallback } from "react";
import ScrollReveal from "@/components/common/ScrollReveal";
import Tabs, { TAB_ORIENTATION } from "@/components/common/Tab";
import AIDICSTabs from "./AIDICSTabs";

interface AidicsStep {
  id: string;
  name: string;
  value: string;
  number: number;
  description: string;
}

const aidicsSteps: AidicsStep[] = [
  {
    id: "step-1",
    name: "Assess",
    value: "assess",
    number: 1,
    description:
      "Perform paper-based and scenario based penetration test to identify immediate gaps, vulnerabilities, and attack path.",
  },
  {
    id: "step-2",
    name: "Identify",
    value: "identify",
    number: 2,
    description:
      "Identify critical assets, risks, and missing controls based on ISO 27001, NIST CSF, and NIST SP-800-82-R2.",
  },
  {
    id: "step-3",
    name: "Design",
    value: "design",
    number: 3,
    description:
      "Provide risk treatment and design improvement plan together with technical configuration guidelines.",
  },
  {
    id: "step-4",
    name: "Inspect",
    value: "inspect",
    number: 4,
    description:
      "Inspect and validate the newly implemented controls through security review and assume breach assessment.",
  },
  {
    id: "step-5",
    name: "Coach",
    value: "coach",
    number: 5,
    description:
      "Provide security awareness training and technical security training to ensure all people in the plant can follow and maintain security posture.",
  },
  {
    id: "step-6",
    name: "Simulate",
    value: "simulate",
    number: 6,
    description:
      "Build a simulated process network to allow OT/ICS customer to safely use XACT attack simulation engine to continuously simulate attacks.",
  },
];

const OTSecurity = () => {
  const [activeStep, setActiveStep] = useState("assess");

  // Manual selection only — no auto-cycling between steps.
  const handleStepChange = useCallback((value: string) => {
    setActiveStep(value);
  }, []);

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pt-2">
      <div className="max-w-[1300px] w-full mx-auto relative z-10">
        <div className="flex flex-col gap-10 lg:gap-12">
          {/* Intro paragraphs — Figma renders them as one 640px text block where
              paragraphs are separated by an empty 28px line, so the gap between
              <p> is one line-height, not 16px. */}
          <ScrollReveal direction="up" delay={100}>
            <div className="max-w-[640px] flex flex-col gap-6 sm:gap-7 text-text-default body-1">
              <p>
                <span className="text-text-highlight">Operational Technology (OT)</span> and{" "}
                <span className="text-text-highlight">Industrial Control Systems (ICS)</span> are
                components that support industrial processes found in many critical infrastructures
                such as energy, oil and gas, chemical, and water.
              </p>
              <p>
                State-sponsored attackers are increasingly interested in these targets due to the
                lack of security by design and significant impact that they can cause if
                successfully breached. Critical infrastructures always give the highest priority to
                high availability and are very reluctant to change and patch. Moreover, the
                inherently vulnerable ICS devices and protocols cannot withstand even a simple
                attack that would otherwise be blocked or prevented in the IT environment. All these
                factors make critical infrastructures become very attractive targets for the
                adversaries.
              </p>
              <p>
                Based on the extensive experiences in offensive security assessment and consultation
                of process networks, ECQ designs its own framework{" "}
                <span className="text-primary font-semibold">AIDICS</span> that supports and helps
                customers in the OT/ICS sector to achieve better security to withstand external,
                internal, and accidental attacks.
              </p>
            </div>
          </ScrollReveal>

          {/* AIDICS Diagram */}
          <ScrollReveal direction="up" delay={200}>
            <div>
              <AIDICSTabs
                steps={aidicsSteps.map((s) => ({
                  id: s.id,
                  name: s.name,
                  value: s.value,
                  number: s.number,
                  content: null,
                }))}
                currentValue={activeStep}
                onChange={handleStepChange}
              />
            </div>
          </ScrollReveal>

          {/* Step list + content panel — shared vertical Tabs, synced with the
              AIDICS diagram. Same component & styling as Secure Code Review so
              the two "steps + description" sections stay consistent. */}
          <ScrollReveal direction="up" delay={300}>
            <Tabs
              tabs={aidicsSteps.map((s) => ({
                id: s.id,
                name: s.name,
                value: s.value,
                number: s.number,
                content: <p>{s.description}</p>,
              }))}
              orientation={TAB_ORIENTATION.VERTICAL}
              showContent
              currentValue={activeStep}
              onChange={handleStepChange}
            />
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default OTSecurity;
